/**
 * AdjustStockModal.tsx
 *
 * Modal to adjust stock for a specific inventory product.
 * Shows current stock, adjustment form with preview, and recent adjustment history.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import {
  adjustStock,
  getAdjustmentHistory,
  type Product,
  type AdjustmentType,
  type ManualAdjustmentType,
  type AdjustStockPayload,
} from '@/services/inventory.service';
import { getErrorMessage } from '@/services/apiClient';
import { WHOLE_UNITS_MESSAGE, checkStockQuantity } from './stockQuantity';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface AdjustStockModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  product: Product;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const ADJUSTMENT_TYPE_OPTIONS: { value: ManualAdjustmentType; label: string }[] = [
  { value: 'add',        label: 'Add Stock' },
  { value: 'remove',     label: 'Remove Stock' },
  { value: 'sale',       label: 'Sale' },
  { value: 'return',     label: 'Return' },
  { value: 'shrinkage',  label: 'Shrinkage / Loss' },
  { value: 'adjustment', label: 'Manual Adjustment (set to)' },
];

/**
 * Types available below user level 3. The API refuses any adjustment that lowers
 * the count (restoring legacy `inventory_adjust_save.php` L45), so offering the
 * rest here would only produce a 403 after the user filled the form in.
 *
 * `adjustment` is excluded because a set-to may raise or lower depending on the
 * number typed — the server decides on the resolved delta, and there is no way
 * to know which it will be while the field is still being edited.
 */
const ADDITIVE_ONLY_TYPES: ManualAdjustmentType[] = ['add', 'return'];

/** Returns true when the operation adds to stock rather than subtracts. */
function isAdditive(type: ManualAdjustmentType): boolean {
  return type === 'add' || type === 'return';
}

/** Compute the new quantity preview given adjustment type and qty. */
function computeNewQty(currentQty: number, type: ManualAdjustmentType, qty: number): number {
  if (type === 'adjustment') return qty;
  if (isAdditive(type)) return currentQty + qty;
  return currentQty - qty;
}

function formatAdjType(type: AdjustmentType): string {
  const map: Record<AdjustmentType, string> = {
    add: 'Add',
    remove: 'Remove',
    sale: 'Sale',
    return: 'Return',
    shrinkage: 'Shrinkage',
    adjustment: 'Adjustment',
    purchase_receive: 'Purchase Receive',
  };
  return map[type] ?? type;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function AdjustStockModal({ open, onClose, onSaved, product }: AdjustStockModalProps) {
  const { isAdmin } = useAuth();
  const availableTypes = useMemo(
    () => (isAdmin
      ? ADJUSTMENT_TYPE_OPTIONS
      : ADJUSTMENT_TYPE_OPTIONS.filter((o) => ADDITIVE_ONLY_TYPES.includes(o.value))),
    [isAdmin],
  );

  const [adjType, setAdjType] = useState<ManualAdjustmentType>('add');
  const [quantityInput, setQuantityInput] = useState('');
  const [notes, setNotes] = useState('');
  const [serverError, setServerError] = useState<string | null>(null);
  const [qtyError, setQtyError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setAdjType('add');
    setQuantityInput('');
    setNotes('');
    setServerError(null);
    setQtyError(null);
  }, [open, product.productId]);

  // ── Adjustment history ────────────────────────────────────────────────────
  const {
    data: history = [],
    isLoading: historyLoading,
  } = useQuery({
    queryKey: ['inventory-history', product.productId],
    queryFn: () => getAdjustmentHistory(product.productId, 10),
    enabled: open,
    staleTime: 30 * 1000,
  });

  // ── Mutation ──────────────────────────────────────────────────────────────
  const mutation = useMutation({
    mutationFn: (payload: AdjustStockPayload) => adjustStock(product.productId, payload),
    onSuccess: () => {
      onSaved();
    },
    onError: (err) => {
      setServerError(getErrorMessage(err, 'The stock could not be adjusted. Please try again.'));
    },
  });

  // ── Derived preview values ────────────────────────────────────────────────
  const currentQty = product.stockQuantity;
  const qtyCheck = checkStockQuantity(quantityInput);
  const newQty = qtyCheck.ok ? computeNewQty(currentQty, adjType, qtyCheck.quantity) : null;
  const willGoBelowZero = newQty != null && newQty < 0;
  // A fraction disables Apply, so say why as soon as it is typed rather than
  // leaving a dead button.
  const shownQtyError =
    qtyError ?? (!qtyCheck.ok && qtyCheck.reason === 'fraction' ? WHOLE_UNITS_MESSAGE : null);

  // ── Handlers ──────────────────────────────────────────────────────────────

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setServerError(null);
    setQtyError(null);

    if (!qtyCheck.ok) {
      setQtyError(
        qtyCheck.reason === 'missing'
          ? 'Quantity is required.'
          : qtyCheck.reason === 'fraction'
            ? WHOLE_UNITS_MESSAGE
            : 'Enter a positive number.',
      );
      return;
    }

    const payload: AdjustStockPayload = {
      adjustmentType: adjType,
      quantity: qtyCheck.quantity,
      notes: notes.trim() || undefined,
    };
    mutation.mutate(payload);
  }

  function handleClose() {
    if (mutation.isPending) return;
    onClose();
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  if (!open) return null;

  const inputCls =
    'border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 w-full';
  const labelCls = 'block text-sm font-medium text-slate-700 mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/40" onClick={handleClose} />

      {/* Modal card */}
      <div className="relative z-10 bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Adjust Stock</h2>
            <p className="text-sm text-slate-500">{product.name}</p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={mutation.isPending}
            className="text-slate-400 hover:text-slate-600 transition-colors text-xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto px-6 py-5 flex-1 space-y-6">

          {/* Current stock display */}
          <div className="rounded-xl bg-slate-50 border border-slate-200 px-5 py-4 flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                Current Stock
              </p>
              <p className="mt-0.5 text-3xl font-bold text-slate-900">
                {currentQty.toLocaleString()}
              </p>
            </div>
            {product.stockQuantity <= product.lowStockAlert && (
              <span className="inline-flex items-center rounded-full bg-amber-100 px-3 py-1 text-sm font-medium text-amber-800">
                Low Stock
              </span>
            )}
          </div>

          {/* Adjustment form */}
          <form id="adjust-form" onSubmit={handleSubmit} noValidate className="space-y-4">
            {serverError && (
              <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                {serverError}
              </div>
            )}

            {/* Adjustment type */}
            <div>
              <label htmlFor="adj-type" className={labelCls}>
                Adjustment Type <span className="text-red-500">*</span>
              </label>
              <select
                id="adj-type"
                value={adjType}
                onChange={(e) => setAdjType(e.target.value as ManualAdjustmentType)}
                className={inputCls}
              >
                {availableTypes.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              {!isAdmin && (
                <p className="mt-1 text-xs text-slate-500">
                  Lowering stock needs a manager account.
                </p>
              )}
            </div>

            {/* Quantity */}
            <div>
              <label htmlFor="adj-qty" className={labelCls}>
                Quantity <span className="text-red-500">*</span>
              </label>
              <input
                id="adj-qty"
                type="number"
                inputMode="numeric"
                min="1"
                step="1"
                value={quantityInput}
                onChange={(e) => {
                  setQuantityInput(e.target.value);
                  if (qtyError) setQtyError(null);
                }}
                className={`${inputCls} ${shownQtyError ? 'border-red-400 focus:ring-red-400 focus:border-red-400' : ''}`}
                placeholder="Enter quantity…"
              />
              {shownQtyError && <p className="mt-1 text-xs text-red-600">{shownQtyError}</p>}
            </div>

            {/* Notes */}
            <div>
              <label htmlFor="adj-notes" className={labelCls}>Notes</label>
              <textarea
                id="adj-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className={inputCls}
                placeholder="Optional notes about this adjustment…"
              />
            </div>

            {/* Preview */}
            {newQty != null && (
              <div
                className={`rounded-xl px-4 py-3 border ${
                  willGoBelowZero
                    ? 'bg-red-50 border-red-200'
                    : 'bg-indigo-50 border-indigo-200'
                }`}
              >
                <p className="text-sm font-medium text-slate-700 mb-1">Preview</p>
                <div className="flex items-center gap-3 text-sm">
                  <span className="text-slate-500">
                    Current: <span className="font-semibold text-slate-800">{currentQty}</span>
                  </span>
                  <span className="text-slate-400">→</span>
                  <span className={willGoBelowZero ? 'text-red-700 font-semibold' : 'text-indigo-700 font-semibold'}>
                    {adjType === 'adjustment' ? `Set to ${newQty}` : `New Qty: ${newQty}`}
                  </span>
                </div>
                {willGoBelowZero && (
                  <p className="mt-1.5 text-xs text-red-600 font-medium">
                    Warning: this adjustment would bring stock below zero.
                  </p>
                )}
              </div>
            )}
          </form>

          {/* Adjustment history */}
          <div>
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Recent Adjustment History</h3>

            {historyLoading && (
              <p className="text-sm text-slate-400">Loading history…</p>
            )}

            {!historyLoading && history.length === 0 && (
              <p className="text-sm text-slate-400">No adjustments recorded yet.</p>
            )}

            {!historyLoading && history.length > 0 && (
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="w-full text-xs text-left">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="px-3 py-2 font-medium text-slate-600">Date</th>
                      <th className="px-3 py-2 font-medium text-slate-600">Type</th>
                      <th className="px-3 py-2 font-medium text-slate-600 text-right">Qty Change</th>
                      <th className="px-3 py-2 font-medium text-slate-600 text-right">New Qty</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {history.map((adj) => {
                      const qtyChange = adj.newQty - adj.previousQty;
                      const isPositive = qtyChange >= 0;
                      return (
                        <tr key={adj.adjustmentId} className="hover:bg-slate-50">
                          <td className="px-3 py-2 text-slate-600 whitespace-nowrap">
                            {formatDate(adj.createdAt)}
                          </td>
                          <td className="px-3 py-2">
                            <span
                              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                                adj.adjustmentType === 'add' || adj.adjustmentType === 'return'
                                  ? 'bg-green-100 text-green-800'
                                  : adj.adjustmentType === 'adjustment'
                                  ? 'bg-slate-100 text-slate-700'
                                  : 'bg-red-100 text-red-700'
                              }`}
                            >
                              {formatAdjType(adj.adjustmentType)}
                            </span>
                          </td>
                          <td className={`px-3 py-2 text-right font-medium ${isPositive ? 'text-green-700' : 'text-red-700'}`}>
                            {isPositive ? '+' : ''}{qtyChange}
                          </td>
                          <td className="px-3 py-2 text-right font-semibold text-slate-800">
                            {adj.newQty}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200">
          <button
            type="button"
            onClick={handleClose}
            disabled={mutation.isPending}
            className="px-4 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 text-slate-700 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="adjust-form"
            disabled={mutation.isPending || !qtyCheck.ok}
            className="px-4 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            {mutation.isPending ? 'Saving…' : 'Apply Adjustment'}
          </button>
        </div>
      </div>
    </div>
  );
}
